import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { Person, Email, Phone, Settings, Edit, NotificationsActive } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../types';

interface ProfileFormProps {
  user: User;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ user }) => {
  const { updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: user.nome || '',
    email: user.email || '',
    telefone: user.telefone || '',
    sigame: user.sigame ?? false,
    ticket_novo: user.ticket_novo ?? false,
    telefones_sigame: user.telefones_sigame || '',
    sigame_hora_inicio: user.sigame_hora_inicio || '07:00',
    sigame_hora_fim: user.sigame_hora_fim || '22:00',
  });

  useEffect(() => {
    setFormData({
      nome: user.nome || '',
      email: user.email || '',
      telefone: user.telefone || '',
      sigame: user.sigame ?? false,
      ticket_novo: user.ticket_novo ?? false,
      telefones_sigame: user.telefones_sigame || '',
      sigame_hora_inicio: user.sigame_hora_inicio || '07:00',
      sigame_hora_fim: user.sigame_hora_fim || '22:00',
    });
  }, [user]);

  const getPerfilDisplayName = (perfil: string) => {
    const perfis: Record<string, string> = {
      'administrador': 'Administrador',
      'financeiro': 'Financeiro',
      'suporte': 'Suporte',
      'cliente': 'Cliente',
      'bot': 'Bot'
    };
    return perfis[perfil] || perfil;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      await updateProfile(formData);
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      nome: user.nome || '',
      email: user.email || '',
      telefone: user.telefone || '',
      sigame: user.sigame ?? false,
      ticket_novo: user.ticket_novo ?? false,
      telefones_sigame: user.telefones_sigame || '',
      sigame_hora_inicio: user.sigame_hora_inicio || '07:00',
      sigame_hora_fim: user.sigame_hora_fim || '22:00',
    });
  };

  const hasChanges = () => {
    return (
      formData.nome !== user.nome ||
      formData.email !== user.email ||
      formData.telefone !== (user.telefone || '') ||
      formData.sigame !== (user.sigame ?? false) ||
      formData.ticket_novo !== (user.ticket_novo ?? false) ||
      formData.telefones_sigame !== (user.telefones_sigame || '') ||
      formData.sigame_hora_inicio !== (user.sigame_hora_inicio || '07:00') ||
      formData.sigame_hora_fim !== (user.sigame_hora_fim || '22:00')
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <Edit color="primary" />
        <Typography variant="h6" fontWeight="bold">
          Informações Pessoais
        </Typography>
      </Box>
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nome Completo"
              value={formData.nome}
              size="small"
              onChange={handleChange('nome')}
              required
              InputProps={{
                startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              value={formData.email}
              size="small"
              onChange={handleChange('email')}
              type="email"
              required
              InputProps={{
                startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Telefone"
              value={formData.telefone}
              size="small"
              onChange={handleChange('telefone')}
              InputProps={{
                startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Perfil"
              value={getPerfilDisplayName(user.perfil)}
              disabled
              size="small"
              InputProps={{
                startAdornment: <Settings sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <NotificationsActive color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                Siga-me (Atendimentos)
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Receba notificações no WhatsApp quando um novo ticket for aberto.
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.sigame}
                  onChange={(e) => setFormData(prev => ({ ...prev, sigame: e.target.checked }))}
                />
              }
              label="Ativar Siga-me"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.ticket_novo}
                  onChange={(e) => setFormData(prev => ({ ...prev, ticket_novo: e.target.checked }))}
                  disabled={!formData.sigame}
                />
              }
              label="Notificar novo ticket"
            />
          </Grid>

          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              label="Telefones Siga-me"
              value={formData.telefones_sigame}
              onChange={handleChange('telefones_sigame')}
              disabled={!formData.sigame}
              size="small"
              placeholder="5511999999999,5531888888888"
              helperText="DDI + DDD + número, separados por vírgula"
            />
          </Grid>

          <Grid item xs={6} sm={2.5}>
            <TextField
              fullWidth
              label="Horário inicial"
              type="time"
              value={formData.sigame_hora_inicio}
              onChange={handleChange('sigame_hora_inicio')}
              disabled={!formData.sigame}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
          </Grid>

          <Grid item xs={6} sm={2.5}>
            <TextField
              fullWidth
              label="Horário final"
              type="time"
              value={formData.sigame_hora_fim}
              onChange={handleChange('sigame_hora_fim')}
              disabled={!formData.sigame}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              //helperText="Notificações só são enviadas dentro deste intervalo (horário de Brasília)"
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading || !hasChanges()}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !hasChanges()}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default ProfileForm; 